import { IndexedAsyncStorageCache } from '@/services/_shared/IndexedAsyncStorageCache';
import { seriesCacheKey, normalizeId } from '@/utils/cacheKeys';

interface SeriesMetadata {
  serie_id: number;
  title: string;
  description: string;
  url_covers: string;
  [key: string]: any;
}

const SERIES_CACHE_INDEX_KEY = '@series_metadata_index';
const SERIES_METADATA_TTL = 24 * 60 * 60 * 1000;

class SeriesMetadataCacheService extends IndexedAsyncStorageCache<SeriesMetadata> {
  constructor() {
    super({
      indexKey: SERIES_CACHE_INDEX_KEY,
      logTag: '🎬 [SERIES-METADATA-CACHE]',
      defaultTtlMs: SERIES_METADATA_TTL,
    });
  }

  private generateCacheKey(campaignId: string, seriesId: string | number): string {
    return seriesCacheKey(normalizeId(seriesId), campaignId);
  }

  async getSeriesMetadata(campaignId: string, seriesId: string | number): Promise<SeriesMetadata | null> {
    return this.getRaw(this.generateCacheKey(campaignId, seriesId));
  }

  async getBulkSeriesMetadata(
    campaignId: string,
    seriesIds: (string | number)[]
  ): Promise<Map<number, SeriesMetadata>> {
    await this.initialize();
    const results = new Map<number, SeriesMetadata>();
    await Promise.all(
      seriesIds.map(async (seriesId) => {
        const metadata = await this.getSeriesMetadata(campaignId, seriesId);
        if (metadata) {
          const numericId = typeof seriesId === 'string' ? parseInt(seriesId, 10) : seriesId;
          results.set(numericId, metadata);
        }
      })
    );
    return results;
  }

  async setSeriesMetadata(campaignId: string, seriesMetadata: SeriesMetadata): Promise<void> {
    await this.setRaw(this.generateCacheKey(campaignId, seriesMetadata.serie_id), seriesMetadata);
  }

  async setBulkSeriesMetadata(campaignId: string, seriesMetadataList: SeriesMetadata[]): Promise<void> {
    await Promise.all(seriesMetadataList.map((m) => this.setSeriesMetadata(campaignId, m)));
  }

  async remove(campaignId: string, seriesId: string | number): Promise<void> {
    await this.removeRaw(this.generateCacheKey(campaignId, seriesId));
  }
}

export const seriesMetadataCacheService = new SeriesMetadataCacheService();
export type { SeriesMetadata };
